import { EligibilityFormQuestion } from "../API/visaEligibilityAPI";

export type EligibilityResultStatus = "pass" | "warn" | "fail";

type ParsedAnswers = {
  [key: EligibilityFormQuestion["id"]]: number | number[];
};

const isAnswerCorrect = (
  question: EligibilityFormQuestion,
  answerId: number | number[] | undefined
): boolean | null => {
  if (answerId == null) return null;

  if (question.multiSelect && Array.isArray(answerId)) {
    if (answerId.length === 0) return null;
    return answerId.every((selectedOptionId) => {
      const option = question.options?.find((o) => o.id === selectedOptionId);
      return option?.isCorrect === true;
    });
  }

  if (typeof answerId !== "number") return null;
  const answer = question.options?.find((o) => o.id === answerId);
  if (!answer) return null;
  return answer.isCorrect === true;
};

/**
 * Derive pass / warn / fail from contributing eligibility answers.
 * Warn = at least one incorrect answer on an incorrectAsWarning question,
 * and no hard-fail incorrect answers.
 */
export const computeEligibilityResultStatus = (
  questions: EligibilityFormQuestion[],
  parsedAnswers: ParsedAnswers
): EligibilityResultStatus => {
  let hasWarn = false;
  let hasFail = false;

  for (const question of questions) {
    if (!question.contributesToEligibility) continue;

    const answeredCorrectly = isAnswerCorrect(
      question,
      parsedAnswers[question.id]
    );
    if (answeredCorrectly === null || answeredCorrectly === true) continue;

    if (question.incorrectAsWarning) {
      hasWarn = true;
    } else {
      hasFail = true;
    }
  }

  if (hasFail) return "fail";
  if (hasWarn) return "warn";
  return "pass";
};

/** Display text of the option(s) the applicant selected for a question. */
const getSelectedOptions = (
  question: EligibilityFormQuestion,
  answerId: number | number[] | undefined
) => {
  if (answerId == null) return [];
  if (question.multiSelect && Array.isArray(answerId)) {
    return question.options?.filter((o) => answerId.includes(o.id)) || [];
  }
  if (typeof answerId !== "number") return [];
  const option = question.options?.find((o) => o.id === answerId);
  return option ? [option] : [];
};

/**
 * Concern copy for incorrect warn-tier contributing answers.
 *
 * The selected option's approved explanation is preferred over the shared
 * question-level warning: two answers on the same question can carry different
 * approved guidance (the felony follow-up is the clearest case), and the
 * question warning would flatten them into one line.
 */
export const getEligibilityConcerns = (
  questions: EligibilityFormQuestion[],
  parsedAnswers: ParsedAnswers
): string[] => {
  const concerns: string[] = [];

  for (const question of questions) {
    if (!question.contributesToEligibility || !question.incorrectAsWarning) {
      continue;
    }

    const answeredCorrectly = isAnswerCorrect(
      question,
      parsedAnswers[question.id]
    );
    if (answeredCorrectly !== false) continue;

    const selected = getSelectedOptions(question, parsedAnswers[question.id]);
    const candidates = selected
      .map((option) => (option.explanation || "").trim())
      .filter(Boolean);
    const texts = candidates.length
      ? candidates
      : [(question.warning || "").trim()].filter(Boolean);

    for (const text of texts) {
      if (!concerns.includes(text)) concerns.push(text);
    }
  }

  return concerns;
};
